import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from 'react';

/** A side of the map a widget sits along, as an editor's panels do. */
export type Side = 'left' | 'right' | 'bottom';

/** Where along a side: its place among the widgets there. */
export interface Docked {
  side: Side;
  index: number;
}

/** Where a widget let go would sit, and the box shown for it while it is dragged. */
export interface Drop extends Docked {
  box: { left: number; top: number; width: number; height: number };
}

interface Docks {
  left: string[];
  right: string[];
  bottom: string[];
  size: Record<Side, number>;
}

export const SIDES: readonly Side[] = ['left', 'right', 'bottom'];
const DOCKS = 'trippath-docks';
/** How near an edge a widget is let go to sit along it. */
const NEAR = 48;
/** A side's width or height until it is made wider or narrower; and how far it can be. */
const FIRST: Record<Side, number> = { left: 360, right: 360, bottom: 200 };
const LEAST = 160;
/** The map is always seen: a side at the left or right is at most this much of the frame's
 * width, at the bottom this much of its height. */
const MOST_ACROSS = 0.35;
const MOST_HIGH = 0.5;

function docksOf(): Docks {
  const empty: Docks = { left: [], right: [], bottom: [], size: { ...FIRST } };
  try {
    const raw = localStorage.getItem(DOCKS);
    const kept: unknown = raw === null ? null : JSON.parse(raw);
    if (typeof kept !== 'object' || kept === null) return empty;
    const ids = (side: Side) => {
      const list: unknown = Reflect.get(kept, side);
      return Array.isArray(list) ? list.filter((id) => typeof id === 'string') : [];
    };
    const sizes: unknown = Reflect.get(kept, 'size');
    const size = (side: Side) => {
      const value: unknown =
        typeof sizes === 'object' && sizes ? Reflect.get(sizes, side) : null;
      return typeof value === 'number' ? value : FIRST[side];
    };
    return {
      left: ids('left'),
      right: ids('right'),
      bottom: ids('bottom'),
      size: { left: size('left'), right: size('right'), bottom: size('bottom') },
    };
  } catch {
    return empty;
  }
}

function keep(docks: Docks): void {
  try {
    localStorage.setItem(DOCKS, JSON.stringify(docks));
  } catch {
    // A private window keeps no layout: it holds for this visit only.
  }
}

/** Forgets which widgets sit along the sides, in this browser. */
export function forgetDocks(): void {
  try {
    localStorage.removeItem(DOCKS);
  } catch {
    // Nothing was kept to forget.
  }
}

/** What a widget asks of the frame it is in. */
export interface Dock {
  /** Where it sits along a side, or `null` over the map. */
  at: (id: string) => Docked | null;
  /** The side's panel it is drawn into. */
  slot: (side: Side) => HTMLElement;
  /** Sits it along a side, or over the map with `null`. */
  put: (id: string, to: Docked | null) => void;
  /** Where it would sit let go at a point of the screen, if near a side; shown until let go. */
  aim: (x: number, y: number) => Drop | null;
  /** Where the map's area is on the screen. */
  origin: () => { left: number; top: number };
}

const DockContext = createContext<Dock | null>(null);

/** The frame a widget is in: there is always one. */
export function useDock(): Dock {
  const dock = useContext(DockContext);
  if (!dock) throw new Error('A widget is drawn in a DockFrame.');
  return dock;
}

/**
 * The map's area with a panel on each side, as an editor's: a widget let go near a side sits
 * along it, the map made smaller for it — its legend and its dock above it — and two along one
 * side share it. Each side is made wider or narrower by its inner edge, and this browser
 * remembers it all. On a phone the sides stand under the map, one after the other (CSS).
 */
export function DockFrame({
  canvas,
  children,
}: {
  /** The map's area, once it is there. */
  canvas: (node: HTMLDivElement | null) => void;
  children: ReactNode;
}) {
  const [docks, setDocks] = useState(docksOf);
  // Each side's panel; until it is there, one apart from the page.
  const [slots, setSlots] = useState<Record<Side, HTMLElement>>(() => ({
    left: document.createElement('div'),
    right: document.createElement('div'),
    bottom: document.createElement('div'),
  }));
  const [drop, setDrop] = useState<Drop | null>(null);
  const frame = useRef(document.createElement('div'));
  const area = useRef(document.createElement('div'));
  const stretch = useRef<{ side: Side; x: number; y: number; size: number } | null>(null);

  const change = (next: Docks) => {
    setDocks(next);
    keep(next);
  };
  const at = (id: string): Docked | null => {
    for (const side of SIDES) {
      const index = docks[side].indexOf(id);
      if (index >= 0) return { side, index };
    }
    return null;
  };
  const put = (id: string, to: Docked | null) => {
    const next: Docks = {
      left: docks.left.filter((one) => one !== id),
      right: docks.right.filter((one) => one !== id),
      bottom: docks.bottom.filter((one) => one !== id),
      size: docks.size,
    };
    if (to) next[to.side].splice(to.index, 0, id);
    change(next);
    setDrop(null);
  };
  // Near a side, or over its panel: there, among the widgets along it by where the pointer is.
  const aim = (x: number, y: number): Drop | null => {
    const whole = frame.current.getBoundingClientRect();
    const near = (side: Side) => {
      const panel = slots[side].getBoundingClientRect();
      const over =
        panel.width > 0 &&
        x >= panel.left &&
        x <= panel.right &&
        y >= panel.top &&
        y <= panel.bottom;
      const edge =
        side === 'left'
          ? x - whole.left < NEAR
          : side === 'right'
            ? whole.right - x < NEAR
            : whole.bottom - y < NEAR;
      return over || edge;
    };
    const side = SIDES.find(near);
    if (side === undefined) {
      setDrop(null);
      return null;
    }
    const panel = slots[side].getBoundingClientRect();
    // The sides stand over the bottom, never over what sits along it.
    const bottom = slots.bottom.getBoundingClientRect();
    const above = (bottom.width > 0 ? bottom.top : whole.bottom) - whole.top;
    // Its panel as it is, or as it will be: its size along the frame's edge.
    const room =
      panel.width > 0
        ? panel
        : side === 'left'
          ? new DOMRect(whole.left, whole.top, docks.size.left, above)
          : side === 'right'
            ? new DOMRect(whole.right - docks.size.right, whole.top, docks.size.right, above)
            : new DOMRect(
                whole.left,
                whole.bottom - docks.size.bottom,
                whole.width,
                docks.size.bottom,
              );
    const shown = [...slots[side].querySelectorAll(':scope > .widget')];
    const across = side === 'bottom';
    const index = shown.filter((one) => {
      const box = one.getBoundingClientRect();
      return across ? box.left + box.width / 2 < x : box.top + box.height / 2 < y;
    }).length;
    const share = (across ? room.width : room.height) / (shown.length + 1);
    const found: Drop = {
      side,
      index,
      box: {
        left: room.left - whole.left + (across ? share * index : 0),
        top: room.top - whole.top + (across ? 0 : share * index),
        width: across ? share : room.width,
        height: across ? room.height : share,
      },
    };
    setDrop(found);
    return found;
  };
  const dock: Dock = {
    at,
    slot: (side) => slots[side],
    put,
    aim,
    origin: () => area.current.getBoundingClientRect(),
  };

  // A side's inner edge, dragged: wider or narrower, never past most of the frame.
  const onEdgeDown = (side: Side) => (event: PointerEvent<HTMLDivElement>) => {
    stretch.current = { side, x: event.clientX, y: event.clientY, size: docks.size[side] };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onEdgeMove = (event: PointerEvent<HTMLDivElement>) => {
    const from = stretch.current;
    if (!from) return;
    const whole = frame.current.getBoundingClientRect();
    const by =
      from.side === 'left'
        ? event.clientX - from.x
        : from.side === 'right'
          ? from.x - event.clientX
          : from.y - event.clientY;
    const most = from.side === 'bottom' ? whole.height * MOST_HIGH : whole.width * MOST_ACROSS;
    const size = Math.round(Math.max(LEAST, Math.min(from.size + by, Math.max(LEAST, most))));
    change({ ...docks, size: { ...docks.size, [from.side]: size } });
  };
  const onEdgeUp = () => {
    stretch.current = null;
  };

  const hold = useCallback(
    (side: Side) => (node: HTMLDivElement | null) => {
      if (node) setSlots((was) => (was[side] === node ? was : { ...was, [side]: node }));
    },
    [],
  );
  const panel = (side: Side) => (
    <div
      key={side}
      className={`plan-dock plan-dock-${side}`}
      // The map always seen, however the window is made: as its inner edge holds a side.
      style={
        side === 'bottom'
          ? { height: docks.size.bottom, maxHeight: '50vh' }
          : { width: docks.size[side], maxWidth: '35vw' }
      }
    >
      <div ref={hold(side)} className="plan-dock-widgets" />
      {/* Its inner edge, for a pointer: the side wider or narrower. */}
      <div
        className="plan-dock-edge"
        aria-hidden="true"
        onPointerDown={onEdgeDown(side)}
        onPointerMove={onEdgeMove}
        onPointerUp={onEdgeUp}
      />
    </div>
  );

  return (
    <DockContext.Provider value={dock}>
      <div
        className="plan-frame"
        ref={(node) => {
          if (node) frame.current = node;
        }}
      >
        {panel('left')}
        <div
          className="plan-canvas"
          ref={(node) => {
            if (node) area.current = node;
            canvas(node);
          }}
        >
          {children}
        </div>
        {panel('right')}
        {panel('bottom')}
        {drop && <div className="dock-ghost" aria-hidden="true" style={drop.box} />}
      </div>
    </DockContext.Provider>
  );
}
